import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
  LOCALE_ID,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';

import { routes } from './app.routes';
import { authInterceptor } from '@core/auth/auth.interceptor';
import { timeoutInterceptor } from '@core/auth/timeout.interceptor';
import { AuthService } from '@core/auth/auth.service';
import { environment } from '@env';
import { provideTaiga } from '@taiga-ui/core';

import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideRouter(
      routes,
      // Дефолт `onSameUrlNavigation: 'ignore'` нам подходит — не дёргаем re-mount
      // на повторный клик по текущему URL. `withComponentInputBinding()` не
      // используем: ни в одном маршруте нет параметров.
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    ),

    provideHttpClient(withFetch(), withInterceptors([timeoutInterceptor, authInterceptor])),

    provideOAuthClient(),
    // Токены живут в localStorage — иначе новая вкладка попадает в login-flow
    // вместо того, чтобы наследовать сессию (см. `AuthService.constructor`,
    // multi-tab logout-broadcast).
    { provide: OAuthStorage, useFactory: () => localStorage },

    // ru-локаль регистрируется через initializer (а не top-level вызов),
    // чтобы импорт `app.config.ts` не имел побочных эффектов: tree-shaking
    // и юнит-тесты остаются предсказуемыми. Initializer выполняется до
    // bootstrap первого CD, гарантируя готовность DatePipe/DecimalPipe.
    provideAppInitializer(() => {
      registerLocaleData(localeRu);
    }),

    provideAppInitializer(() => {
      const authService = inject(AuthService);
      return authService.initKeycloak();
    }),

    provideAnimations(),
    provideTaiga(),
    { provide: LOCALE_ID, useValue: 'ru' },

    // База URL для Router и [routerLink]. Берётся из environment, чтобы при
    // деплое на subpath (например /inventory/) достаточно было поправить
    // `baseHref` в `environment.production.ts` без правки index.html.
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
  ],
};
